export function combineArrays<T>(...arrays: T[][]): T[] | TypedArray {
  if (arrays.length < 1) {
    return [];
  }

  if (ArrayBuffer.isView(arrays[0])) {
    return combineTypedArrays(arrays as unknown as TypedArray[]);
  }

  return combineNonTypedArrays<T>(arrays);
}

function combineNonTypedArrays<T>(arrays: T[][]): T[] {
  const CHUNK_SIZE = 10_000;
  const ret = arrays[0];
  for (const array of arrays.slice(1)) {
    for (let i = 0; i < array.length; i += CHUNK_SIZE) {
      const chunk = array.slice(i, i + CHUNK_SIZE);
      Array.prototype.push.apply(ret, chunk);
    }
  }
  return ret;
}

type NumberTypedArray =
  | Int8Array
  | Uint8Array
  | Uint8ClampedArray
  | Int16Array
  | Uint16Array
  | Int32Array
  | Uint32Array
  | Float32Array
  | Float64Array;

type BigIntTypedArray = BigInt64Array | BigUint64Array;

export type TypedArray = NumberTypedArray | BigIntTypedArray;

function isBigIntArray(arr: TypedArray): arr is BigIntTypedArray {
  return arr instanceof BigInt64Array || arr instanceof BigUint64Array;
}

function combineTypedArrays(arrays: TypedArray[]): TypedArray {
  const first = arrays[0];
  for (const arr of arrays) {
    if (arr.constructor !== first.constructor) {
      throw new TypeError(
        "All typed arrays passed to combineArrays must have the same data type",
      );
    }
  }
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);

  if (isBigIntArray(first)) {
    const Constructor = first.constructor as new (
      length: number,
    ) => BigIntTypedArray;
    const result = new Constructor(totalLength);
    let offset = 0;
    for (const arr of arrays as BigIntTypedArray[]) {
      result.set(arr, offset);
      offset += arr.length;
    }
    return result;
  } else {
    const Constructor = first.constructor as new (
      length: number,
    ) => NumberTypedArray;
    const result = new Constructor(totalLength);
    let offset = 0;
    for (const arr of arrays as NumberTypedArray[]) {
      result.set(arr, offset);
      offset += arr.length;
    }
    return result;
  }
}
